import { lightTheme, RainbowKitProvider, useConnectModal } from "@rainbow-me/rainbowkit";
import { QueryClientProvider } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { MotionConfig } from "motion/react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useAccountEffect, WagmiProvider } from "wagmi";
import { Page } from "../components/app-ui";
import { ConnectButton, useAuthState } from "../components/wallet";
import { createQueryClient } from "../lib/query-client";
import { signOutLocal, useSession } from "../lib/session";
import { wagmiConfig } from "../lib/wagmi";
import { APP_LINKS, SiteFooter, SiteHeader } from "../site/layout";
import "@rainbow-me/rainbowkit/styles.css";
import "../app/app.css";

export const Route = createFileRoute("/app")({
  // `?connect` (from the site header) opens the connect dialog straight away.
  validateSearch: (search: Record<string, unknown>): { connect?: boolean } =>
    search.connect === true || search.connect === "true" ? { connect: true } : {},
  component: AppLayout,
});

/** RainbowKit in the site's colours: lime accent with ink on top, rounded, Inter Tight. */
const walletTheme = {
  ...lightTheme({
    accentColor: "#ccff00",
    accentColorForeground: "#000000",
    borderRadius: "large",
    overlayBlur: "small",
  }),
  fonts: { body: '"Inter Tight Variable", ui-sans-serif, system-ui, sans-serif' },
};

/** The wallet stack lives here, so only the app loads wagmi, viem, RainbowKit and the connectors. */
function AppLayout() {
  const session = useSession();
  return (
    <WagmiProvider config={wagmiConfig}>
      <SessionQueries key={session?.token ?? "signed-out"}>
        <RainbowKitProvider
          theme={walletTheme}
          modalSize="compact"
          appInfo={{ appName: "bips", learnMoreUrl: "/docs" }}
        >
          <AppShell />
        </RainbowKitProvider>
      </SessionQueries>
    </WagmiProvider>
  );
}

/** A fresh cache for each sign-in prevents one wallet seeing another wallet's cached vaults. */
function SessionQueries({ children }: { children: ReactNode }) {
  const [client] = useState(createQueryClient);
  useEffect(
    () => () => {
      void client.cancelQueries();
      client.clear();
    },
    [client],
  );
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}

function AppShell() {
  // Disconnecting from RainbowKit's account modal also ends the bips session.
  useAccountEffect({ onDisconnect: signOutLocal });
  const { isConnected, restoring } = useAuthState();
  const { connect } = Route.useSearch();
  const { openConnectModal } = useConnectModal();
  // `?connect` from the site header: open the wallet list once, as soon as RainbowKit is ready.
  const opened = useRef(false);
  useEffect(() => {
    if (connect && !isConnected && openConnectModal && !opened.current) {
      opened.current = true;
      openConnectModal();
    }
  }, [connect, isConnected, openConnectModal]);
  return (
    <MotionConfig reducedMotion="user">
      <div className="vx ap flex min-h-dvh flex-col overflow-x-clip">
        <SiteHeader wallet={<ConnectButton />} links={APP_LINKS} />
        <main className="flex-1 pt-[72px]">{restoring ? <Restoring /> : <Outlet />}</main>
        <SiteFooter />
      </div>
    </MotionConfig>
  );
}

function Restoring() {
  return (
    <Page>
      <div className="grid min-h-[40vh] place-items-center">
        <p className="flex items-center gap-3 text-[16px] text-(--ap-grey)" role="status">
          <Loader2 className="size-5 animate-spin text-(--ap-pink)" aria-hidden="true" />
          Opening your vaults…
        </p>
      </div>
    </Page>
  );
}
